/**
 * Parent Portal Dashboard
 *
 * Main dashboard for parents to:
 * - View their assigned children (students)
 * - Access and complete consent forms
 * - Track consent form submissions
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge, Button } from 'react-bootstrap';
import { BsPeople, BsFileText, BsCheckCircle } from 'react-icons/bs';
import { FaEye, FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import parentService from '../../services/parentService';
import ConsentFormsList from './ConsentFormsList';
import config from '../../config/config';
import './componentCss/ParentDashboard.css';

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [consentForms, setConsentForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load children and consent forms in parallel
      const [childrenData, formsData] = await Promise.all([
        parentService.getMyChildren(),
        parentService.getConsentForms()
      ]);

      setChildren(childrenData.children || []);
      setConsentForms(formsData.forms || []);

      // Auto-select first child if only one
      if (childrenData.children && childrenData.children.length === 1) {
        setSelectedChild(childrenData.children[0]);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleChildSelect = (child) => {
    setSelectedChild(child);
  };

  const handleConsentSubmitted = () => {
    // Reload data after successful submission
    loadDashboardData();
    toast.success('Consent form submitted successfully');
  };

  const handleViewChildDetails = (child) => {
    // Navigate to patient details page in view-only mode
    navigate(`${config.baseUrl}patient-details/${child.id}`);
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading parent dashboard...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="parent-dashboard">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="dashboard-title">
            <BsPeople className="me-2" />
            Parent Portal
          </h2>
          <p className="text-muted">Manage consent forms for your children</p>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Children List */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <BsPeople className="me-2" />
              My Children ({children.length})
            </Card.Header>
            <Card.Body>
              {children.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  No children assigned to your account. Please contact the school administrator.
                </Alert>
              ) : (
                <Row>
                  {children.map((child) => (
                    <Col md={6} lg={4} key={child.id} className="mb-3">
                      <Card
                        className={`child-card ${selectedChild?.id === child.id ? 'selected' : ''}`}
                      >
                        <Card.Body>
                          <div onClick={() => handleChildSelect(child)} style={{ cursor: 'pointer' }}>
                            <h5 className="mb-1">
                              <FaUser className="me-2" />
                              {child.demographics?.first_name} {child.demographics?.last_name}
                            </h5>
                            <p className="text-muted small mb-1">
                              MRN: {child.mrn_permanent || child.mrn_temporary}
                            </p>
                            <p className="text-muted small mb-2">
                              Grade: {child.demographics?.grade || 'N/A'}
                            </p>
                            {selectedChild?.id === child.id && (
                              <Badge bg="primary" className="mb-2">
                                <BsCheckCircle className="me-1" />
                                Selected
                              </Badge>
                            )}
                          </div>
                          <div className="d-grid gap-2 mt-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewChildDetails(child)}
                            >
                              <FaEye className="me-1" />
                              View Details
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Consent Forms Section */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-success text-white">
              <BsFileText className="me-2" />
              Consent Forms ({consentForms.length})
            </Card.Header>
            <Card.Body>
              {!selectedChild ? (
                <Alert variant="warning" className="mb-0">
                  Please select a child above to view and complete consent forms.
                </Alert>
              ) : consentForms.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  No consent forms available at this time.
                </Alert>
              ) : (
                <ConsentFormsList
                  forms={consentForms}
                  selectedChild={selectedChild}
                  onSubmitSuccess={handleConsentSubmitted}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ParentDashboard;
